import { startInactiveSpan } from '@sentry/capacitor'
import { SPAN_STATUS_ERROR } from '@sentry/core'
import { Span } from '@sentry/types'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { isIOS, isSafari } from 'react-device-detect'

import {
  GachaRollType,
  Maybe,
  PurchasableGachaTicket,
  RollGachaMutation,
} from '@/clients/utoniq-core/schema'
import {
  generateApng,
  playApngAnimation,
  VideoFullScreenRef,
} from '@/components/lib/animation'
import { config, constants } from '@/config'
import { useMessage, useRouter, useTranslation } from '@/core'
import { useAnalytics } from '@/core/analytics'
import { ConversionCategories } from '@/core/analytics/ConversionKeys'
import { captureError } from '@/core/errors'
import { getPlatform } from '@/core/native/app'
import { useLocationWatcher } from '@/core/native/geolocation'

import { useViewModel } from '../viewModel'

/**
 * @description
 * viewModelの状態とテンプレートの状態・挙動を合わせて管理するフック
 */

export const useTemplateStore = ({
  state,
  actions,
}: ReturnType<typeof useViewModel>) => {
  const { t } = useTranslation('gachaDetail')
  const { router, routerPages, generatePath, routerNames } =
    useRouter<{ artistId: string }>()
  const { showMessage, showError } = useMessage()
  const { analytics } = useAnalytics()
  const rollingGachaTransactionRef = useRef<Span | null>(null)

  /** サイドメニュー開閉状態 */
  const [openSideMenu, setOpenSideMenu] = useState<boolean>(false)
  /** ガチャチケット購入のドロワー表示状態 */
  const [openGachaTicketPurchaseDrawer, setOpenGachaTicketPurchaseDrawer] =
    useState<boolean>(false)
  /** ポイントチャージ用ドロワーの表示状態 */
  const [openPointPurchaseDrawer, setOpenPointPurchaseDrawer] =
    useState<boolean>(false)
  /** チケットでガチャを回すことを確認するモーダルの表示状態 */
  const [openConfirmByTicket, setOpenConfirmByTicket] = useState<boolean>(false)
  /** ポイントでガチャを回すことを確認するモーダルの表示状態 */
  const [openConfirmByPoint, setOpenConfirmByPoint] = useState<boolean>(false)
  /** チケット・ポイント両対応のガチャで、ガチャチケ不足時にポイントでガチャを引けるモーダルの表示状態 */
  const [openInsufficientTicket, setOpenInsufficientTicket] =
    useState<boolean>(false)
  /**
   * ポイントでガチャを引く際に、ポイントが不足している場合に表示されるモーダルの表示状態
   */
  const [openInsufficientPointModal, setOpenInsufficientPointModal] =
    useState<boolean>(false)
  /** チケット購入時に表示されるモーダルの表示状態 */
  const [openTicketPurchaseConfirmModal, setOpenTicketPurchaseConfirmModal] =
    useState<boolean>(false)
  /** チケット購入後に表示されるモーダルの表示状態 */
  const [openTicketPurchaseCompleteModal, setOpenTicketPurchaseCompleteModal] =
    useState<boolean>(false)
  /** チケット購入エラーが発生した時のモーダルの表示状態 */
  const [openTicketPurchaseErrorModal, setOpenTicketPurchaseErrorModal] =
    useState<boolean>(false)
  /** ガチャチケット購入時、購入するチケットの枚数の状態 */
  const [purchasableGachaTicket, setPurchasableGachaTicket] =
    useState<Maybe<Omit<PurchasableGachaTicket, 'id'>>>(null)
  /** ガチャを回す際、それが1回まわすものか10連ガチャかどうかの状態(number) */
  const [gachaCount, setGachaCount] = useState<number>(0)
  /** ガチャを回している状態かどうか */
  const [gachaRolling, setGachaRolling] = useState<boolean>(false)
  /** スタンバイ状態かどうか */
  const [standingBy, setStandingBy] = useState<boolean>(false)
  /** スタンバイ・本動画含めて動画を再生している時間かどうか */
  const [playingAnimation, setPlayingAnimation] = useState<boolean>(false)
  /** 動画スキップ処理 */
  const [handleSkip, setHandleSkip] = useState({ fn() {} })

  const lastGachaOrderIdRef = useRef<string | undefined>()

  /** ガチャ一覧の先頭、ルーティングが/gachas/latestの時に遷移するもの */
  const latestGacha = state.gachas?.edges.length
    ? state.gachas?.edges[0].node
    : null

  /** 現在カルーセル表示しているガチャのインデックス番号 */
  const initialSlide = useMemo<number | undefined>(() => {
    if (!state.gachas?.edges) return

    if (state.gachaId === 'latest') return 0

    return state.gachas.edges.findIndex(({ node }) => node.id === state.gachaId)
  }, [latestGacha?.id, state.gachaId, state?.gachas?.edges])

  /** 現在カルーセルで表示しているガチャのデータ */
  const currentGacha = useMemo(() => {
    if (initialSlide === undefined) {
      return null
    }
    return state.gachas?.edges?.[initialSlide]?.node ?? null
  }, [state.gachas, initialSlide])

  /** GPSガチャの状態 */
  const { permission, currentLocation, requestPermission, watch, unwatch } =
    useLocationWatcher()

  // 動画再生コンポーネントのrefオブジェクトを作成する
  const videoFullscreenRef = useRef<VideoFullScreenRef>(null)
  const standbyVideoFullscreenRef = useRef<VideoFullScreenRef>(null)

  useEffect(() => {
    if (!currentGacha?.gpsData) return
    requestPermission().catch(showError)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentGacha?.gpsData, requestPermission])

  useEffect(() => {
    if (!permission) return
    watch().catch(showError)
    return () => {
      unwatch().catch(showError)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [permission, unwatch, watch])

  /** APNG再生プレイヤーのRef */
  const apngFullScreenRef = useRef<HTMLCanvasElement>(null)
  const [apngAnimationPlaying, setApngAnimationPlaying] =
    useState<boolean>(false)

  /** カルーセルをスライドした際の挙動 */
  const handleAfterChange = useCallback(
    (currentSlide: number) => {
      if (!state.gachas?.edges) return

      const currentGacha = state.gachas.edges[currentSlide]

      if (!currentGacha) return

      router.replace({
        pathname: generatePath(routerPages.ArtistGachaDetail, {
          artistId:
            (config.applicationType === 'standalone'
              ? config.artistId
              : router.query.artistId) ?? '#',
          gachaId: currentGacha.node.id,
        }),
        search: location.search,
      })
    },
    [
      state.gachas?.edges,
      router,
      generatePath,
      routerPages.ArtistGachaDetail,
      location.search,
    ]
  )

  /** ガチャチケットを購入する処理 */
  const handlePurchaseGachaTicket = useCallback(async () => {
    if (!purchasableGachaTicket) {
      setOpenTicketPurchaseConfirmModal(false)
      return
    }

    analytics?.track('click_purchaseByPoint', {
      category: `${ConversionCategories.gachaTicket}/GachaDetail`,
      quantity: purchasableGachaTicket.count,
      point: purchasableGachaTicket.prepaidPoints ?? 0,
      gachaTicketId: `organization:${config.organizationId}`,
    })

    await actions
      .onPurchaseGachaTicket(purchasableGachaTicket)
      .then(() => {
        analytics?.track('purchaseByPoint', {
          category: `${ConversionCategories.gachaTicket}/GachaDetail`,
          quantity: purchasableGachaTicket.count,
          point: purchasableGachaTicket.prepaidPoints ?? 0,
          gachaTicketId: `organization:${config.organizationId}`,
        })
        setOpenTicketPurchaseConfirmModal(false)
        setOpenTicketPurchaseCompleteModal(true)
      })
      .catch((e: any) => {
        setOpenTicketPurchaseConfirmModal(false)
        // ポイント不足の場合、モーダルを表示する
        if (
          e?.graphQLErrors?.[0]?.extensions?.errorCode ===
          constants.errorCodes.INSUFFICIENT_PREPAID_POINT_BALANCE
        ) {
          analytics?.track('error_insufficientPrepaidPoint', {
            applicationKey: config.applicationKey,
            prepaidPointId: state.prepaidPointBalance?.prepaidPointId,
            gachaId: currentGacha?.id ?? '',
            name: 'gachaDetail',
          })
          return setOpenTicketPurchaseErrorModal(true)
        }
        showError(e)
      })
  }, [
    purchasableGachaTicket,
    actions.onPurchaseGachaTicket,
    analytics,
    showError,
    currentGacha?.id,
    state.prepaidPointBalance,
  ])

  /** ガチャチケット購入のアクションシートをキャンセルするに呼ばれる処理 */
  const onCancelPurchaseGachaTicket = () => {
    analytics?.track('click_cancel', {
      applicationKey: config.applicationKey,
      gachaId: currentGacha?.id,
      artistId: currentGacha?.artistId,
      gachaTicketId: `organization:${config.organizationId}`,
      type: 'bottomsheet',
      name: 'purchaseGachaTicket',
    })
    setOpenGachaTicketPurchaseDrawer(false)
  }

  /** チケット確認モーダルをキャンセルで閉じる際に呼ばれる処理 */
  const onCancelPurchaseGachaTicketModal = () => {
    analytics?.track('click_cancel', {
      applicationKey: config.applicationKey,
      gachaId: state.gachaId,
      artistId: currentGacha?.artistId,
      count: purchasableGachaTicket?.count,
      gachaTicketId: `organization:${config.organizationId}`,
      prepaidPointId: state.prepaidPointBalance?.prepaidPointId,
      type: 'modal',
      name: 'purchaseGachaTicket',
    })
    setOpenTicketPurchaseConfirmModal(false)
  }

  /** チケット購入時にポイント不足によるエラーモーダルを閉じる際に呼ばれる処理 */
  const onCancelPurchaseErrorGachaTicket = () => {
    analytics?.track('click_cancel', {
      applicationKey: config.applicationKey,
      gachaId: currentGacha?.id,
      artistId: currentGacha?.artistId,
      gachaTicketId: `organization:${config.organizationId}`,
      prepaidPointId: state.prepaidPointBalance?.prepaidPointId,
      type: 'modal',
      name: 'purchaseGachaTicket',
    })

    setOpenTicketPurchaseErrorModal(false)
  }

  /** ポイント購入処理 */
  const handlePurchasePoint = actions.onPurchaseCharge

  /** ガチャを回すボタンをクリックしたときの処理。条件に応じたモーダルやメッセージを表示する */
  const handleClickGachaTrigger = async (count: number) => {
    /** 0.回すガチャの個数に関する状態を更新 */
    setGachaCount(count)

    /**
     * 1.スタンバイ動画がある場合、スタンバイ動画のvideo要素にURLをセットする
     */
    const standByAnimationUri = currentGacha?.standByAnimations
      ? currentGacha?.standByAnimations[count === 10 ? 'bulk' : 'single']?.uri
      : null
    if (standByAnimationUri) {
      standbyVideoFullscreenRef?.current?.init((state: any) => ({
        ...state,
        url: standByAnimationUri,
        playsinline: true,
        onReady() {
          standbyVideoFullscreenRef.current?.handleOnReadyVideo()
        },
        onEnded() {
          setStandingBy(true)
        },
        onError(e: Error) {
          captureError(e, 'gachaDetailPlayGachaAnimationVideo')
          //check videoFullscreen have src value
          if (videoFullscreenRef?.current?.src) {
            videoFullscreenRef?.current?.playVideo()
          }
        },
      }))
    }

    /**
     * 2.ポイントでしか引けないガチャの場合
     *
     */
    if (currentGacha?.gachaRollType === GachaRollType.PrepaidPoint) {
      /**
       * 2-1.所持ポイントが足りない場合、ポイント購入モーダルを表示
       */
      if (
        (state?.prepaidPointBalance?.balance ?? 0) <
        (currentGacha?.price ?? 0) * count
      ) {
        setOpenInsufficientPointModal(true)
        return
      }

      /**
       * 2-2.所持ポイントが足りている場合、回す確認モーダル
       */
      setOpenConfirmByPoint(true)
      return
    }

    /**
     * 3.値段設定がないガチャ（ガチャチケットのみ許容のガチャの場合）
     *
     */
    if (!currentGacha?.price) {
      /**
       * 3-1.ガチャチケットが足りない場合
       */
      if (state.ticket === null || count > Number(state.ticket)) {
        /**
         * 3-1-1.ポイントを使用しないアプリの場合、チケットが足りませんとメッセージを表示
         */
        if (!config.modules.prepaidPoint) {
          showMessage(t('notEnoughGachaTicket'))
          return
        }

        /**
         * 3-1-2.ポイントを使用するアプリの場合、ガチャチケット購入モーダルを表示
         */
        setOpenGachaTicketPurchaseDrawer(true)
        return
      }

      /**
       * 3-2.ガチャチケットが足りている場合、ガチャチケットで回す
       */
      setOpenConfirmByTicket(true)
      return
    }

    /**
     * 4.ガチャチケット・ポイント両方許容のガチャの場合（両方許容且つ値段設定があるガチャの場合）
     *
     */
    /**
     * 4-1.ガチャチケットが足りない場合
     */
    if (state.ticket === null || count > Number(state.ticket)) {
      /**
       * 4-1-1.1回引く場合、ポイントで引くか促すモーダルを表示
       */
      if (count === 1) {
        // ポイントが不足している場合、ポイント購入モーダルを表示
        if (
          (state?.prepaidPointBalance?.balance ?? 0) <
          currentGacha.price * count
        ) {
          setOpenGachaTicketPurchaseDrawer(true)
          return
        } else {
          setOpenInsufficientTicket(true)
          return
        }
      }

      /**
       * 4-1-2.10回引く場合、ガチャチケット購入モーダルを表示
       */
      setOpenGachaTicketPurchaseDrawer(true)
      return
    }

    /**
     * 4-2.所持チケットがが足りている場合、ガチャを回す確認モーダル表示
     */
    setOpenConfirmByTicket(true)
  }

  /** ガチャ処理終了後、結果画面へ遷移する処理 */
  const redirectToGachaComplete = useCallback(
    (count: number, gachaResultData?: RollGachaMutation | null) => {
      standbyVideoFullscreenRef?.current?.pauseVideo()
      videoFullscreenRef?.current?.pauseVideo()
      if (
        lastGachaOrderIdRef.current ===
        gachaResultData?.rollGacha?.gachaOrder?.id
      ) {
        return
      }
      lastGachaOrderIdRef.current = gachaResultData?.rollGacha?.gachaOrder?.id
      rollingGachaTransactionRef.current?.end()
      return router.push(
        generatePath(
          count === 10
            ? routerPages.TenGachaResult
            : routerPages.SingleGachaResult,
          {
            gachaId: gachaResultData?.rollGacha?.gachaOrder?.gachaId,
            gachaOrderId: gachaResultData?.rollGacha?.gachaOrder?.id,
          }
        )
      )
    },
    [
      generatePath,
      router,
      routerPages.SingleGachaResult,
      routerPages.TenGachaResult,
    ]
  )

  /**
   * ガチャを回す処理
   * ガチャを回すのに成功した場合：アニメーションの表示は成功・失敗に関わらずガチャ結果画面へと遷移させる。
   * ガチャを回すのに失敗した場合：スナックバーでエラー表示
   */
  const handleRollGacha = async (rollType: GachaRollType) => {
    if (!currentGacha?.id) return

    setGachaRolling(true)
    rollingGachaTransactionRef.current = startInactiveSpan({
      name: currentGacha.isLimited
        ? 'rollingGachaLimited'
        : 'rollingGachaLimitless',
      forceTransaction: true,
    })
      .setAttribute('gachaCount', gachaCount)
      .setAttribute('gachaId', currentGacha.id)
      .setAttribute('rollType', rollType)

    const callAPISpan = startInactiveSpan({
      name: 'callAPI',
      parentSpan: rollingGachaTransactionRef.current,
    })

    await actions
      .onRollGacha(gachaCount, currentGacha.id, rollType)
      .then(async ({ data: gachaResultData }) => {
        callAPISpan.end()
        /** スキップ処理を「ガチャ結果先へのリダイレクト」に設定する */
        setHandleSkip({
          fn() {
            setStandingBy(false)
            setPlayingAnimation(false)
            setApngAnimationPlaying(false)
            setGachaRolling(false)
            redirectToGachaComplete(gachaCount, gachaResultData)
          },
        })

        analytics?.track('click_rollGacha', {
          applicationKey: config.applicationKey,
          gachaId: currentGacha.id,
          artistId: state.artist?.id,
          /**
           * @description
           * カスタムディメンションは文字列しか計測しないため、数値を文字列変換する
           * https://app.clickup.com/t/908127/UT-9829
           */
          count: gachaCount.toString(),
          gachaTicketId: `organization:${config.organizationId}`,
          type: rollType,
        })

        /**
         * @description
         * ガチャのアニメーションを再生する処理
         * URLから動画ファイルがAPNGかMP4か判断し、ファイル種別ごとの処理を行う。
         * URLの取得に失敗、またはアニメーション再生に失敗した時はSentry通知だけして結果画面に遷移させる
         */
        const rewardAnimationUri =
          gachaResultData?.rollGacha.gachaOrder.rewardAnimation?.mediumUri ||
          gachaResultData?.rollGacha.gachaOrder.rewardAnimation?.uri

        // アニメーションのリソースがない場合、結果画面にリダイレクト
        if (!rewardAnimationUri)
          return redirectToGachaComplete(gachaCount, gachaResultData)

        /**
         * 動画ファイルがAPNGの場合
         * ミューテーション結果のファイルをAPNGプレイヤー要素で再生する
         */
        if (!isVideoExt(rewardAnimationUri)) {
          const animationApng = await generateApng(rewardAnimationUri).catch(
            e => {
              captureError(e, 'gachaDetailGenerateApng')
              rollingGachaTransactionRef.current?.setStatus({
                code: SPAN_STATUS_ERROR,
                message: 'gachaDetailGenerateApngError',
              })
              return
            }
          )
          if (animationApng) {
            setApngAnimationPlaying(true)
            await playApngAnimation(apngFullScreenRef, animationApng)
              .then(() => {
                setGachaRolling(false)
              })
              .catch(e => {
                captureError(e, 'gachaDetailplayApngAnimation')
                rollingGachaTransactionRef.current?.setStatus({
                  code: SPAN_STATUS_ERROR,
                  message: 'gachaDetailPlayApngAnimationError',
                })
              })
              .finally(() => {
                setApngAnimationPlaying(false)
              })
          }
          return redirectToGachaComplete(gachaCount, gachaResultData)
        }

        /**
         * 動画ファイルがMP4の場合
         * 結果アニメーション動画のvideo要素にURLをセットする
         */
        if (!videoFullscreenRef || !videoFullscreenRef?.current)
          return redirectToGachaComplete(gachaCount, gachaResultData)
        videoFullscreenRef?.current?.init((state: any) => ({
          ...state,
          url: rewardAnimationUri,
          playsinline: true,
          onReady() {
            videoFullscreenRef.current?.handleOnReadyVideo()
          },
          onEnded() {
            setPlayingAnimation(false)
            redirectToGachaComplete(gachaCount, gachaResultData)
          },
          onError(e: Error) {
            setPlayingAnimation(false)
            captureError(e, 'gachaDetailPlayGachaAnimationVideo')
            rollingGachaTransactionRef.current?.setStatus({
              code: SPAN_STATUS_ERROR,
              message: 'gachaDetailPlayGachaAnimationVideoError',
            })
            redirectToGachaComplete(gachaCount, gachaResultData)
          },
        }))

        setPlayingAnimation(true)
        /**
         * スタンバイ動画がある場合、スタンバイ動画を再生してreturn
         */
        if (currentGacha?.standByAnimations) {
          standbyVideoFullscreenRef?.current?.playVideo()
          return
        }
        /**
         * スタンバイ動画がない場合、そのままアニメーションを再生
         */
        videoFullscreenRef?.current?.playVideo()
      })
      .catch(e => {
        showError(e)
        rollingGachaTransactionRef.current
          ?.setStatus({ code: SPAN_STATUS_ERROR, message: 'internal_error' })
          .end()
        captureError(e, 'gachaDetail')
      })
      .finally(() => {
        setOpenConfirmByTicket(false)
        setOpenConfirmByPoint(false)
        setOpenInsufficientTicket(false)
        setGachaRolling(false)
      })
  }

  // スタンバイ状態からトリガーを引き、ガチャ動画を再生する
  const handleRollGachaAfterStandingby = () => {
    setStandingBy(false)
    // アニメーション動画を再生
    videoFullscreenRef?.current?.playVideo()
  }

  return {
    state: {
      ...state,
      title: initialSlide
        ? [state.gachas?.edges[initialSlide]?.node.name]
        : [routerNames.ArtistGachaDetail, state.artist?.displayName],
      gachaRolling,
      standingBy,
      playingAnimation,
      apngAnimationPlaying,
      gpsClientInfo: {
        permission,
        currentLocation,
      },
      videoFullscreenRef,
      standbyVideoFullscreenRef,
      apngFullScreenRef,
      gachaCount,
      latestGacha,
      initialSlide,
      currentGacha,
      openSideMenu,
      openGachaTicketPurchaseDrawer,
      openPointPurchaseDrawer,
      openConfirmByTicket,
      openConfirmByPoint,
      openInsufficientTicket,
      openInsufficientPointModal,
      openTicketPurchaseConfirmModal,
      openTicketPurchaseCompleteModal,
      openTicketPurchaseErrorModal,
      purchasableGachaTicket,
      isSafari: getPlatform() === 'ios' || isSafari || isIOS,
    },
    actions: {
      setOpenSideMenu,
      setOpenGachaTicketPurchaseDrawer,
      setOpenPointPurchaseDrawer,
      setOpenConfirmByTicket,
      setOpenConfirmByPoint,
      setOpenInsufficientTicket,
      setOpenInsufficientPointModal,
      setOpenTicketPurchaseConfirmModal,
      setOpenTicketPurchaseCompleteModal,
      setPurchasableGachaTicket,
      handleAfterChange,
      handlePurchaseGachaTicket,
      handlePurchasePoint,
      onCancelPurchaseGachaTicket,
      onCancelPurchaseErrorGachaTicket,
      onCancelPurchaseGachaTicketModal,
      onSkip: handleSkip.fn,
      handleClickGachaTrigger,
      handleRollGachaByTicket: async () =>
        handleRollGacha(GachaRollType.GachaTicket),
      handleRollGachaByPoint: async () =>
        handleRollGacha(GachaRollType.PrepaidPoint),
      handleRollGachaAfterStandingby,
    },
  }
}
/** 動画フォーマットがmp4かどうか判定する */
export function isVideoExt(url: string): boolean {
  return url.split(/[#?]/)[0].split('.').pop()?.trim() === 'mp4'
}
